import { createApp } from 'vue'
import { createPinia } from 'pinia'
import { api as sharedApi } from '@adaptcx/shared'

import App from './App.vue'
import router from './router'

// Load the shared editorial design system so the login screen renders
// identically to the other portals (super-admin / tenant-admin /
// agent-assist). Widget-specific styles below override anything that
// would otherwise touch the in-call widget UI.
import '@adaptcx/shared/styles/theme.css'
import '@adaptcx/shared/styles/components.css'
import '@adaptcx/shared/styles/product.css'
import '@adaptcx/shared/styles/portal-app.css'

// Shared `--ax-*` Helix token layer (v2.6.17). Imported AFTER the editorial
// CSS above so the widget speaks the same token vocabulary as the portals
// (super-admin / tenant-admin / agent-assist-portal, which finished its
// `.ax-*` move in v2.6.16). We pull ONLY the token files — not the full
// appshell/index.css barrel, which also carries sidebar/topbar/KPI chrome the
// embedded widget has no use for. Color only: the widget keeps its own flat,
// compact geometry (its `--radius-*`/`--spacing-*`/`--shadow-*` in style.css,
// whose names don't collide with the ax files) so it still reads as a panel
// inside host agent desktops.
import '@adaptcx/shared/styles/appshell/design-tokens.css'
import '@adaptcx/shared/styles/appshell/tokens.css'

import './assets/styles/main.css'
import './assets/styles/style.css'
import './assets/styles/global.css'

// Point the shared axios instance (used by EditorialSplitLoginShell,
// authService, useAuthStore, ssoService, etc.) at the platform auth-API
// URL so `/api/v1/auth/login` and `/api/v1/users/me` resolve correctly.
//
// Resolution priority:
//   1. VITE_AUTH_API_URL if it's a real URL (dev override / non-default)
//   2. Derive from window.location.hostname using the standard
//      AdaptCX pattern: `assist.widget.<env>` → `api.<env>` for prod,
//      `localhost:*` → empty (relative, Vite proxy handles routing)
//   3. Fall through to shared default
//
// The hostname-derivation step exists because the ConfigMap
// substitution of `__API_GATEWAY_URL__` requires terraform to have
// been applied with `api_domain` set — when that hasn't happened (or
// the placeholder is unsubstituted for any other reason), the
// fallback to VITE_API_URL silently routes auth to the connector and
// produces a confusing 404. Deriving from hostname makes it work
// regardless of ConfigMap state.
function resolveAuthApiUrl(): string | null {
  const envUrl = import.meta.env.VITE_AUTH_API_URL as string | undefined
  if (envUrl && /^https?:\/\//.test(envUrl)) {
    return envUrl
  }

  if (typeof window === 'undefined') return null
  const host = window.location.hostname
  if (!host || host === 'localhost' || host === '127.0.0.1') {
    // Local dev — force shared api to relative URLs so the Vite proxy
    // (vite.config.ts) intercepts every /api/* and routes per prefix.
    // The shared api.ts default is http://localhost:8000 which would
    // bypass the proxy; explicit empty string overrides that.
    sharedApi.defaults.baseURL = ''
    return null
  }
  // Production pattern: assist.widget.<customer>.<env>.adaptcx.ai
  //                  → api.<customer>.<env>.adaptcx.ai
  const idx = host.indexOf('assist.widget.')
  if (idx === 0) {
    return `https://api.${host.substring('assist.widget.'.length)}`
  }
  return null
}

const authBaseURL = resolveAuthApiUrl()
if (authBaseURL) {
  sharedApi.defaults.baseURL = authBaseURL
}

const app = createApp(App)

app.use(createPinia())
app.use(router)

app.mount('#app')
